/**
 * Pedido de venda (v6): uma tela em quatro áreas — dados da venda, inclusão rápida de
 * produtos, produtos do pedido, valores e resumo — e um único "Salvar rascunho" que grava
 * tudo numa transação (D13). Também serve a Venda Direta (v7).
 */
import {
  formatValidationIssues,
  salesOrderDraftSchema,
  salesOrderNewSchema,
  type SaleDraftItem,
  type SalesOrder,
} from "@cafegestor/shared";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Alert, Button } from "@/components/ui";
import { ApiRequestError } from "@/lib/api";
import { SaveError } from "../../cadastros/widgets";
import { usePaymentMethods } from "../../compras/lib";
import { useDocAction } from "../../estoque/lib";
import { ORDER_KEY } from "../lib";
import { bodyOf, useSaleDraft } from "./draft";
import { QuickItemEntry, type AddOutcome } from "./QuickItemEntry";
import { SaleHeaderFields } from "./SaleHeaderFields";
import { SaleItemsTable } from "./SaleItemsTable";
import { InstallmentsFields, SaleSummary } from "./SaleSummary";

const INVALIDATE = [ORDER_KEY, ["estoque"], ["financeiro"], ["vendas", "precos"]];

export function Area({
  id,
  title,
  children,
  highlight,
}: {
  id: string;
  title: string;
  children: ReactNode;
  highlight?: boolean;
}) {
  return (
    <section
      aria-labelledby={id}
      className={`space-y-4 rounded-xl border p-5 ${highlight ? "border-caramel/50 bg-caramel-soft/60" : "bg-card"}`}
    >
      <h3 id={id} className="text-base font-semibold">
        {title}
      </h3>
      {children}
    </section>
  );
}

export function SaleOrderForm({
  order,
  baseVersion,
  onCreated,
  onClose,
  onDirtyChange,
  canSellShort = false,
}: {
  order: SalesOrder | null;
  /** versão em que a edição começou (o salvar é recusado se o pedido mudou desde então) */
  baseVersion?: number;
  onCreated: (o: SalesOrder) => void;
  onClose: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  /** v7: quem pode vender sem estoque vê o aviso informativo na inclusão */
  canSellShort?: boolean;
}) {
  const queryClient = useQueryClient();
  const draft = useSaleDraft(order);
  const { header } = draft.state;
  const methods = usePaymentMethods();
  const method = methods.data?.items.find((m) => m.id === header.paymentMethodId);
  // sem forma escolhida: a do pedido (ou ainda não se sabe)
  const receivable = method
    ? method.generatesReceivable
    : order?.expectedPaymentMode
      ? order.expectedPaymentMode === "titulo"
      : null;
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [flashKey, setFlashKey] = useState<string | null>(null);
  const [undo, setUndo] = useState<{ item: SaleDraftItem; index: number } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const customerRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => onDirtyChange?.(draft.dirty), [draft.dirty, onDirtyChange]);
  // sair da página com alterações não salvas: o navegador pergunta
  useEffect(() => {
    if (!draft.dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [draft.dirty]);
  useEffect(() => {
    if (!flashKey) return;
    const id = setTimeout(() => setFlashKey(null), 2000);
    return () => clearTimeout(id);
  }, [flashKey]);
  useEffect(() => {
    if (!undo) return;
    const id = setTimeout(() => setUndo(null), 5000);
    return () => clearTimeout(id);
  }, [undo]);
  useEffect(() => {
    if (!order) customerRef.current?.focus();
  }, [order]);

  const create = useDocAction<unknown, SalesOrder>({
    path: () => "/vendas/pedidos",
    invalidate: INVALIDATE,
    successMessage: (o) => `Pedido ${o.number} criado como rascunho.`,
    onDone: (o) => onCreated(o),
  });
  const save = useDocAction<unknown, SalesOrder>({
    path: () => `/vendas/pedidos/${order?.id ?? ""}/rascunho`,
    method: "PUT",
    invalidate: INVALIDATE,
    successMessage: (o) => `Pedido ${o.number} salvo.`,
    onDone: (o) => draft.reset(o),
  });
  const pending = create.isPending || save.isPending;
  const serverError = (order ? save.error : create.error) ?? null;
  const conflict = serverError instanceof ApiRequestError && serverError.status === 409;

  function submit() {
    if (pending) return;
    if (editingKey) {
      setFormError("Termine (Enter) ou cancele (Esc) a edição do item antes de salvar.");
      return;
    }
    const body = bodyOf(draft, receivable);
    const parsed = order
      ? salesOrderDraftSchema.safeParse({ ...body, version: baseVersion ?? order.version })
      : salesOrderNewSchema.safeParse(body);
    if (!parsed.success) {
      setFormError(formatValidationIssues(parsed.error.issues));
      if (parsed.error.issues.some((i) => i.path[0] === "customerId")) customerRef.current?.focus();
      return;
    }
    setFormError(null);
    // erro do servidor: nada do que foi digitado se perde (o estado da tela fica como está)
    if (order) save.mutate(parsed.data);
    else create.mutate(parsed.data);
  }

  function onKeyDown(ev: KeyboardEvent<HTMLDivElement>) {
    if (ev.key === "Enter" && (ev.ctrlKey || ev.metaKey)) {
      ev.preventDefault();
      submit();
    }
  }

  function onAdd(item: SaleDraftItem): AddOutcome {
    const r = draft.addItem(item);
    if (r.outcome !== "repetido") setFlashKey(r.items[r.index]?.key ?? null);
    setUndo(null);
    return r.outcome;
  }

  const editing = draft.state.items.find((i) => i.key === editingKey) ?? null;

  return (
    <div className="space-y-4" onKeyDown={onKeyDown}>
      <Area id="venda-area-dados" title="1 · Dados da venda">
        <SaleHeaderFields
          header={header}
          order={order}
          customerRef={customerRef}
          onChange={draft.setHeader}
        />
      </Area>

      <Area id="venda-area-inclusao" title="2 · Incluir produto" highlight>
        <QuickItemEntry
          customerId={header.customerId}
          warehouseId={header.warehouseId}
          editing={editing}
          onAdd={onAdd}
          onUpdate={(key, item) => {
            draft.replaceItem(key, item);
            setFlashKey(key);
          }}
          onCancelEdit={() => setEditingKey(null)}
          onNeedCustomer={() => customerRef.current?.focus()}
          canSellShort={canSellShort && header.loadId === ""}
        />
      </Area>

      <Area id="venda-area-itens" title="3 · Produtos do pedido">
        <SaleItemsTable
          items={draft.state.items}
          editingKey={editingKey}
          flashKey={flashKey}
          onEdit={setEditingKey}
          onRemove={(key) => {
            const r = draft.removeItem(key);
            if (editingKey === key) setEditingKey(null);
            if (r) setUndo({ item: r.removed, index: r.index });
          }}
        />
        {undo && (
          <p className="text-sm" role="status">
            {undo.item.productCode} removido.{" "}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                draft.restoreItem(undo.item, undo.index);
                setUndo(null);
              }}
            >
              Desfazer
            </Button>
          </p>
        )}
      </Area>

      <Area id="venda-area-resumo" title="4 · Valores complementares e resumo">
        <SaleSummary
          freight={draft.state.freight}
          other={draft.state.other}
          preview={draft.preview}
          onFreight={draft.setFreight}
          onOther={draft.setOther}
        />
        {receivable === true && (
          <InstallmentsFields
            rows={draft.state.installments}
            onChange={draft.setInstallments}
            total={draft.preview.total}
            orderDate={header.orderDate}
            method={method}
          />
        )}
        {receivable === false && (
          <p className="text-sm text-muted-foreground">
            Forma à vista: na confirmação o valor fica registrado no pedido como recebido (forma,
            valor, data e hora), sem título em Contas a receber.
          </p>
        )}
      </Area>

      <div className="flex flex-wrap items-center gap-3 border-t pt-4">
        {confirmClose ? (
          <span className="flex items-center gap-2 text-sm">
            Descartar as alterações não salvas?
            <Button size="sm" variant="danger" onClick={onClose}>
              Descartar
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setConfirmClose(false)}>
              Continuar editando
            </Button>
          </span>
        ) : (
          <Button
            variant="secondary"
            onClick={() => (draft.dirty ? setConfirmClose(true) : onClose())}
          >
            Cancelar / voltar
          </Button>
        )}
        <span className="ml-auto text-sm text-muted-foreground">
          {draft.dirty ? "Alterações não salvas" : order ? "Tudo salvo" : ""}
        </span>
        <Button onClick={submit} disabled={pending || (order !== null && !draft.dirty)}>
          {pending ? "Salvando..." : "Salvar rascunho (Ctrl+Enter)"}
        </Button>
      </div>
      {formError && <Alert tone="error">{formError}</Alert>}
      {conflict ? (
        <Alert tone="error">
          Este pedido foi alterado por outra pessoa enquanto você editava. O que você digitou
          continua na tela; para ver a versão atual, descarte as alterações.{" "}
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              draft.reset(order);
              save.reset();
              void queryClient.invalidateQueries({ queryKey: ORDER_KEY });
            }}
          >
            Descartar e recarregar
          </Button>
        </Alert>
      ) : (
        <SaveError error={serverError} />
      )}
    </div>
  );
}
